import { FirebaseConfig } from "./deploymentconfig"
import {
  FirebaseApp,
  FirebaseOptions,
  getApp,
  initializeApp,
} from "firebase/app"
import { connectAuthEmulator, getAuth } from "firebase/auth"
import {
  connectFirestoreEmulator,
  initializeFirestore,
} from "firebase/firestore"
import { connectStorageEmulator, getStorage } from "firebase/storage"
import isEqual from "lodash.isequal"

export type GetOrCreateResult<T> = { wasCreated: boolean; value: T }

export function getOrCreateFirebaseAppWithEmulators(
  config: FirebaseConfig,
): GetOrCreateResult<FirebaseApp> {
  const { emulators = {} } = config
  const { wasCreated, value: firebase } = getOrCreateFirebaseApp(config)

  if (wasCreated && emulators.auth) {
    console.debug("Emulating firebase auth...")
    const auth = getAuth(firebase)
    connectAuthEmulator(auth, emulators.auth)
  }

  if (wasCreated && emulators.firestore) {
    console.debug("Emulating firestore...")
    const firestore = initializeFirestore(firebase, {
      // Cypress breaks firestore's connection so we switch it to long polling when running the tests
      experimentalForceLongPolling: !!(window as any).Cypress,
    })

    const url = new URL(emulators.firestore)
    connectFirestoreEmulator(firestore, url.hostname, parseInt(url.port))
  }

  // Setup cloud storage
  if (wasCreated) {
    const storage = getStorage(firebase)

    // If we use the default (10m) the file upload will sit with a spinner for too long
    // (if no connectivity). Setting to 30s.
    storage.maxUploadRetryTime = 30000

    if (emulators.storage) {
      console.debug("Emulating cloud store...")
      const url = new URL(emulators.storage)
      connectStorageEmulator(storage, url.hostname, parseInt(url.port))
    }
  }

  return { wasCreated, value: firebase }
}

function getOrCreateFirebaseApp(
  options: FirebaseOptions,
  name?: string,
): GetOrCreateResult<FirebaseApp> {
  let app: FirebaseApp | undefined
  try {
    app = getApp(name)
  } catch (_) {
    return { wasCreated: true, value: initializeApp(options, name) }
  }

  if (!isEqual(options, app.options)) {
    throw new Error(
      "initializeApp() has already been called with different options",
    )
  }
  return { wasCreated: false, value: app }
}
